import { createFileRoute, redirect } from "@tanstack/react-router";
import { Check, Zap } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { i18nLinks, i18nOgMeta } from "@/lib/seo";
import { client } from "@/orpc/client";

export const Route = createFileRoute("/upgrade")({
  beforeLoad: () => {
    if (import.meta.env.VITE_POLAR_ENABLED !== "true") {
      throw redirect({ to: "/", replace: true });
    }
  },
  head: () => ({
    meta: [
      { title: "Go Pro — Toolverse" },
      {
        name: "description",
        content:
          "Upgrade to Toolverse Pro. Remove all ads, unlock every F1 Fantasy feature, and get access to all future tools — for less than a coffee a month.",
      },
      ...i18nOgMeta("/upgrade"),
    ],
    links: [...i18nLinks("/upgrade")],
  }),
  component: UpgradePage,
});

const PRO_FEATURES = [
  { text: "No ads — ever, on any page", highlight: true },
  { text: "x2 captain slot — always unlocked", highlight: false },
  { text: "x3 chip — always unlocked", highlight: false },
  {
    text: "Budget Boost price predictions — always unlocked",
    highlight: false,
  },
  {
    text: "Every new tool we launch — included at no extra cost",
    highlight: false,
  },
  { text: "Supports independent development", highlight: false },
];

function UpgradePage() {
  const [loading, setLoading] = useState<"monthly" | "annual" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCheckout(plan: "monthly" | "annual") {
    setLoading(plan);
    setError(null);
    try {
      const result = await client.polar.createCheckout({ plan });
      window.location.href = result.url;
    } catch {
      setError("Checkout is not available right now. Please try again later.");
      setLoading(null);
    }
  }

  return (
    <div className="flex-1 bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-5 py-12 md:py-16">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Zap className="h-3 w-3 text-primary" />
            <span className="text-[11px] font-semibold text-primary tracking-wide uppercase">
              Pro
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">
            Play smarter. Win more.
          </h1>
          <p className="text-base text-muted-foreground max-w-md mx-auto">
            Serious F1 Fantasy players don't leave points on the table. Go Pro
            and get every edge — completely ad-free.
          </p>
        </div>

        {/* Features list */}
        <div className="mb-8 rounded-lg border border-border bg-card p-6">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
            Everything in Pro
          </h2>
          <ul className="space-y-3">
            {PRO_FEATURES.map((feature) => (
              <li
                key={feature.text}
                className="flex items-center gap-3 text-sm"
              >
                <Check
                  className={`h-4 w-4 shrink-0 ${feature.highlight ? "text-primary" : "text-muted-foreground"}`}
                />
                <span className={feature.highlight ? "font-medium" : ""}>
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pricing cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-3">
          {/* Monthly */}
          <div className="rounded-lg border border-border bg-card p-6 flex flex-col gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Monthly
              </div>
              <div className="text-3xl font-extrabold tracking-tight">
                $5.99
                <span className="text-base font-normal text-muted-foreground">
                  /month
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Billed monthly. Cancel anytime.
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleCheckout("monthly")}
              disabled={loading !== null}
            >
              {loading === "monthly" ? "Redirecting…" : "Start monthly"}
            </Button>
          </div>

          {/* Annual — visually dominant */}
          <div className="rounded-lg border-2 border-primary bg-card p-6 flex flex-col gap-4 relative">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <span className="text-[11px] font-bold bg-primary text-primary-foreground px-3 py-1 rounded-full uppercase tracking-wide">
                2 months free
              </span>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Annual
              </div>
              <div className="text-3xl font-extrabold tracking-tight">
                $59
                <span className="text-base font-normal text-muted-foreground">
                  /year
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                <span className="line-through text-muted-foreground/60 mr-1">
                  $71.88
                </span>
                $4.92/month — you save $12.88
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => handleCheckout("annual")}
              disabled={loading !== null}
            >
              {loading === "annual" ? "Redirecting…" : "Get 2 months free →"}
            </Button>
          </div>
        </div>

        {/* Annual nudge */}
        <p className="text-xs text-center text-muted-foreground mb-6">
          94% of Pro members choose annual.
        </p>

        {error && (
          <p className="text-sm text-destructive text-center mb-4">{error}</p>
        )}

        <p className="text-xs text-center text-muted-foreground mb-8">
          Payments processed securely by Polar.sh. Taxes handled automatically.
        </p>

        <div className="pt-4 border-t border-border text-center">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
}
